import { inject, Injectable } from '@angular/core';
import { MIN_SAFARI_VERSION } from '@common/constants';
import { Browser } from '@common/enums';
import { WINDOW } from '@common/window.provider';

const SUPPORTED_BROWSERS = new Set<string>([Browser.Chrome, Browser.Firefox, Browser.Edge, Browser.Safari]);
const BROWSER_PATTERNS = [
  { pattern: /Edge|Edg\/([\d.]+)/i, name: Browser.Edge },
  { pattern: /Chrome\/([\d.]+)/i, name: Browser.Chrome },
  { pattern: /Firefox\/([\d.]+)/i, name: Browser.Firefox },
  { pattern: /Safari\/([\d.]+)/i, name: Browser.Safari, versionPattern: /Version\/([\d.]+)/i },
  { pattern: /MSIE ([\d.]+)/i, name: Browser.IE },
  { pattern: /Trident\/.*rv:([\d.]+)/i, name: Browser.IE } // IE 11
];

@Injectable({ providedIn: 'root' })
export class BrowserService {
  private readonly window = inject(WINDOW);

  isUnsupportedSafari(): boolean {
    const { name, version } = this.detectBrowser();

    return name === Browser.Safari && this.compareVersions(version, MIN_SAFARI_VERSION) < 0;
  }

  isSupportedBrowser(): boolean {
    const { name, version } = this.detectBrowser();

    if (SUPPORTED_BROWSERS.has(name)) {
      return name === Browser.Safari ? this.compareVersions(version, MIN_SAFARI_VERSION) >= 0 : true;
    }

    return false;
  }

  detectBrowser(): { name: string; version: string } {
    const { appName, appVersion, userAgent } = this.window.navigator;

    for (const { pattern, name, versionPattern } of BROWSER_PATTERNS) {
      const match = userAgent.match(pattern);

      if (match) {
        let version = match[1] || '';

        if (name === Browser.Safari && versionPattern) {
          version = userAgent.match(versionPattern)?.[1] || version;
        }

        return {
          name,
          version: this.cleanVersion(version)
        };
      }
    }

    return {
      name: appName,
      version: this.cleanVersion(appVersion)
    };
  }

  private cleanVersion(version: string): string {
    return version.split(/[;\s]/)[0];
  }

  compareVersions(version1: string, version2: string): number {
    const v1 = version1.split('.').map(Number);
    const v2 = version2.split('.').map(Number);

    const maxLength = Math.max(v1.length, v2.length);

    for (let i = 0; i < maxLength; i++) {
      const num1 = v1[i] || 0;
      const num2 = v2[i] || 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    return 0;
  }
}
